import { Popconfirm } from 'antd';
import { Formik } from 'formik';
import { type PropsWithChildren, forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import type { MeasureUnitDimensionsValue } from 'src/components/base/fields/SpecificationField/SpecificationDimensionsField/SpecificationDimensionsField.types';
import {
  type SpecificationDimensionsPopupFormValues,
  specificationDimensionsPopupFormValidationSchema,
} from 'src/components/base/fields/SpecificationField/SpecificationDimensionsField/SpecificationDimensionsPopup.validation';
import {
  SpecificationDimensionsPopupFormContent,
  type SpecificationDimensionsPopupFormContentRef,
} from 'src/components/base/fields/SpecificationField/SpecificationDimensionsField/SpecificationDimensionsPopupContent';
import { useModalExt } from 'src/hooks/useModalExt';
import type { SpecificationMeasureUnit } from 'src/types/reference/SpecificationMeasureUnit';

type SpecificationDimensionsPopupFormProps = {
  is3d: boolean;
  multiselect: boolean;
  defaultMeasureUnit: SpecificationMeasureUnit | null;
  allowedMeasureUnits: SpecificationMeasureUnit[];

  onSubmit: (values: SpecificationDimensionsPopupFormValues) => Promise<void> | void;
};

const SpecificationDimensionsPopupForm = forwardRef<
  SpecificationDimensionsPopupFormContentRef,
  SpecificationDimensionsPopupFormProps
>(
  (
    {
      is3d,
      multiselect,
      defaultMeasureUnit,
      allowedMeasureUnits,
      onSubmit,
    }: Readonly<SpecificationDimensionsPopupFormProps>,
    ref
  ) => {
    const specificationDimensionsPopupFormContent = useRef<SpecificationDimensionsPopupFormContentRef>(null);

    useImperativeHandle<SpecificationDimensionsPopupFormContentRef, SpecificationDimensionsPopupFormContentRef>(
      ref,
      () => ({
        submit: specificationDimensionsPopupFormContent.current?.submit,
        isDisabled: specificationDimensionsPopupFormContent.current?.isDisabled,
      }),
      []
    );

    return (
      <Formik<SpecificationDimensionsPopupFormValues>
        initialValues={{
          values: [
            {
              length: null,
              width: null,
              height: null,

              measureUnit: defaultMeasureUnit,
            },
          ],
        }}
        validationSchema={specificationDimensionsPopupFormValidationSchema(is3d)}
        onSubmit={onSubmit}
      >
        {(formikProps) => (
          <SpecificationDimensionsPopupFormContent
            ref={specificationDimensionsPopupFormContent}
            formikProps={formikProps}
            is3d={is3d}
            multiselect={multiselect}
            defaultMeasureUnit={defaultMeasureUnit}
            allowedMeasureUnits={allowedMeasureUnits}
          />
        )}
      </Formik>
    );
  }
);

type SpecificationDimensionsPopupProps = PropsWithChildren<{
  is3d: boolean;
  multiselect: boolean;
  defaultMeasureUnit: SpecificationMeasureUnit | null;
  allowedMeasureUnits: SpecificationMeasureUnit[];

  onClose?: () => void;
  onAddValue: (newCustomValues: MeasureUnitDimensionsValue[]) => void;
}>;

export type SpecificationDimensionsPopupRef = {
  open: () => void;
  close: () => void;
};

export const SpecificationDimensionsPopup = forwardRef<
  SpecificationDimensionsPopupRef,
  SpecificationDimensionsPopupProps
>(({ is3d, multiselect, defaultMeasureUnit, allowedMeasureUnits, onAddValue, onClose, children }, ref) => {
  const specificationDimensionsPopupForm = useRef<SpecificationDimensionsPopupFormContentRef>(null);
  const { modalProps, onOpen, onClose: onModalClose } = useModalExt();

  const handleClose = useCallback(() => {
    onModalClose();

    onClose?.();
  }, [onModalClose, onClose]);

  const handleSuccess = useCallback(
    (values: SpecificationDimensionsPopupFormValues) => {
      onAddValue(values.values as MeasureUnitDimensionsValue[]);

      handleClose();
    },
    [onAddValue, handleClose]
  );

  const handleConfirm = useCallback(() => specificationDimensionsPopupForm.current?.submit?.(), []);

  useImperativeHandle<SpecificationDimensionsPopupRef, SpecificationDimensionsPopupRef>(
    ref,
    () => ({
      open: () => {
        onOpen();
      },
      close: () => {
        handleClose();
      },
    }),
    [onOpen, handleClose]
  );

  useEffect(() => {
    if (modalProps.open) {
      const clickHandler = (event: MouseEvent) => {
        const popupElement = document.getElementsByClassName('specification-dimensions-popup')?.[0];

        if (
          popupElement &&
          // When press Remove item button on last element it becomes removed when this click handler fires and `popupElement.contains` is false
          document.contains(event.target as Element) &&
          (!(event.target instanceof Element) || !popupElement.contains(event.target))
        ) {
          window.removeEventListener('click', clickHandler);

          handleClose();
        }
      };

      // Even from click on Custom button is captured here. Need some time to avoid it
      setTimeout(() => window.addEventListener('click', clickHandler));

      return () => {
        window.removeEventListener('click', clickHandler);
      };
    }
  }, [modalProps.open, handleClose]);

  return (
    <Popconfirm
      placement="top"
      overlayClassName="specification-dimensions-popup"
      open={modalProps.open}
      destroyTooltipOnHide={true}
      title="Add Custom Value"
      description={
        <SpecificationDimensionsPopupForm
          ref={specificationDimensionsPopupForm}
          is3d={is3d}
          multiselect={multiselect}
          defaultMeasureUnit={defaultMeasureUnit}
          allowedMeasureUnits={allowedMeasureUnits}
          onSubmit={handleSuccess}
        />
      }
      okText="Add"
      cancelText="Cancel"
      icon={null}
      onConfirm={handleConfirm}
      onCancel={handleClose}
      okButtonProps={
        specificationDimensionsPopupForm.current?.isDisabled != null
          ? {
              disabled: specificationDimensionsPopupForm.current.isDisabled,
            }
          : undefined
      }
    >
      {children}
    </Popconfirm>
  );
});
